/**
 * "Qaysi hisobdan to'landi?" -- xarajat, maosh, bonus to'lovi va carrier to'lovi tushishi uchun bitta oyna.
 * Hisob valyutasi boshqa bo'lsa summa joriy kurs bilan taxminan ko'rsatiladi (aniq summa -- serverda, to'lov kuni kursi).
 * Oxirgi tanlangan hisob shu qurilmada eslab qolinadi.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { dayjs } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Field, Input, Select, TimeInput } from "@/components/ui/field";
import { Modal } from "@/components/ui/modal";
import { useCurrentRate, useMoneyAccounts } from "./api";
import { CommissionField } from "./CommissionField";
import { pctError, withCommission } from "./commission";
import { convert, fmt, nowTime, num } from "./money";
import type { Currency, MoneyAccount } from "./types";

const LAST_KEY = "dh.finance.lastAccount";
const NONE = "none";

function readLast(): string | null {
  try {
    return localStorage.getItem(LAST_KEY);
  } catch {
    return null;
  }
}

function writeLast(value: string) {
  try {
    localStorage.setItem(LAST_KEY, value);
  } catch {
    /* ixtiyoriy: maxfiy rejim */
  }
}

/** Standart hisob: oxirgi tanlangan (hali bor bo'lsa), bo'lmasa filial + valyuta mos, bo'lmasa valyuta mos, bo'lmasa birinchisi */
export function defaultAccount(accounts: MoneyAccount[], currency: string, branch: number | null | undefined, last: string | null): MoneyAccount | undefined {
  return (
    accounts.find((a) => String(a.id) === last) ??
    accounts.find((a) => a.currency === currency && branch && a.branch === branch) ??
    accounts.find((a) => a.currency === currency) ??
    accounts[0]
  );
}

export interface PayResult {
  account: number | null;
  date: string;
  /** "HH:MM:SS" -- to'lovning aniq vaqti (standart: oyna ochilgan payt) */
  time: string;
  /** Komissiya foizi ("" -- yo'q); hisobsiz belgilashda yuborilmaydi */
  commission_pct?: string;
}

export function PayModal({
  open,
  onOpenChange,
  title,
  description,
  amounts,
  branch,
  direction = "out",
  allowNoAccount,
  confirmLabel,
  loading,
  commissionPct = "",
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** To'lanadigan summalar o'z valyutasida (maoshlar har xil valyutada bo'lishi mumkin) */
  amounts: { amount: number; currency: string }[];
  branch?: number | null;
  /** "in" -- pul hisobga tushadi (carrier to'lovi) */
  direction?: "in" | "out";
  /** Hisobsiz faqat belgilash (pul tizimdan tashqarida o'tgan) */
  allowNoAccount?: boolean;
  confirmLabel: string;
  loading?: boolean;
  /** Komissiya foizi oldindan (xarajatda -- kategoriyadan); maosh/bonusda bo'sh, qo'lda kiritiladi */
  commissionPct?: string;
  onConfirm: (result: PayResult) => Promise<void> | void;
}) {
  const { t } = useTranslation("budget");
  const accounts = useMoneyAccounts(open);
  const rate = useCurrentRate(open);
  const [account, setAccount] = useState("");
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [time, setTime] = useState(nowTime());
  const [commission, setCommission] = useState("");
  const list = useMemo(() => accounts.data ?? [], [accounts.data]);
  const mainCurrency = amounts[0]?.currency ?? "USD";

  useEffect(() => {
    if (!open) return;
    setDate(dayjs().format("YYYY-MM-DD"));
    setTime(nowTime());
    setCommission(commissionPct);
    const def = defaultAccount(list, mainCurrency, branch, readLast());
    setAccount(def ? String(def.id) : allowNoAccount ? NONE : "");
  }, [open, list, mainCurrency, branch, allowNoAccount, commissionPct]);

  const chosen = list.find((a) => String(a.id) === account);
  // Hisob valyutasidagi jami (har summa alohida aylantiriladi); kurs yo'q -- null
  const inAccount = chosen
    ? amounts.reduce<number | null>((sum, a) => {
        const v = convert(a.amount, a.currency as Currency, chosen.currency, rate);
        return sum === null || v === null ? null : sum + v;
      }, 0)
    : null;
  // Komissiya bilan: chiqimda qo'shiladi, kirimda ayriladi (aniq summa -- serverda, har to'lov alohida)
  const net = inAccount !== null ? withCommission(direction, inAccount, commission).net : null;
  const after = chosen && net !== null ? num(chosen.balance) + (direction === "in" ? net : -net) : null;
  // Qoldiq manfiy bo'lmaydi (2026-10-18): yetishmasa tugma o'chiq, sabab ko'rinadi; aniq tekshiruv backend'da (to'lov kuni kursi)
  const short = direction === "out" && after !== null && after < 0 ? -after : 0;
  const byCurrency = amounts.reduce<Record<string, number>>((m, a) => ({ ...m, [a.currency]: (m[a.currency] ?? 0) + a.amount }), {});

  async function confirm() {
    writeLast(account);
    await onConfirm({ account: chosen ? chosen.id : null, date, time, ...(chosen ? { commission_pct: commission } : {}) });
  }

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>{t("common:actions.cancel")}</Button>
          <Button variant="primary" loading={loading} disabled={!account || (account !== NONE && !chosen) || short > 0 || pctError(commission)} onClick={() => void confirm()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="rounded-[10px] bg-fill/40 px-3 py-2 text-[13px]">
          <span className="text-muted">{t(direction === "in" ? "pay.receiving" : "pay.paying")}</span>{" "}
          <span className="font-semibold">
            {Object.entries(byCurrency)
              .map(([c, v]) => fmt(v, c as Currency))
              .join(" + ")}
          </span>
        </div>
        <Field label={t(direction === "in" ? "pay.toAccount" : "pay.fromAccount")} hint={list.length === 0 && !accounts.isPending ? t("pay.noAccounts") : undefined}>
          <Select value={account} onChange={(e) => setAccount(e.target.value)}>
            {!allowNoAccount && !account && <option value="">{t("pay.choose")}</option>}
            {list.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name} · {fmt(a.balance, a.currency)}
                {a.branch_name ? ` · ${a.branch_name}` : ""}
              </option>
            ))}
            {allowNoAccount && <option value={NONE}>{t("pay.noAccount")}</option>}
          </Select>
        </Field>
        {chosen && (
          <CommissionField direction={direction} amount={inAccount ?? 0} currency={chosen.currency} value={commission} onChange={setCommission} />
        )}
        <Field label={t("pay.date")}>
          <div className="flex gap-2">
            <Input type="date" value={date} max={dayjs().format("YYYY-MM-DD")} onChange={(e) => setDate(e.target.value)} />
            <TimeInput seconds value={time} onChange={(e) => setTime(e.target.value)} aria-label={t("pay.time")} />
          </div>
        </Field>
        {chosen && (
          <div className="space-y-0.5 text-[12px] text-ink-soft">
            {Object.keys(byCurrency).some((c) => c !== chosen.currency) && (
              <div>
                {inAccount !== null
                  ? t("pay.converted", { amount: fmt(inAccount, chosen.currency), rate: rate?.toLocaleString("en-US") })
                  : t("pay.noRate")}
              </div>
            )}
            {after !== null && short === 0 && <div>{t("pay.balanceAfter", { before: fmt(chosen.balance, chosen.currency), after: fmt(after, chosen.currency) })}</div>}
            {short > 0 && <div className="font-semibold text-danger">{t("pay.insufficient", { short: fmt(short, chosen.currency) })}</div>}
          </div>
        )}
        {account === NONE && <p className="text-[12px] text-muted">{t("pay.noAccountHint")}</p>}
      </div>
    </Modal>
  );
}
